import {
  InfiniteData,
  infiniteQueryOptions,
  queryOptions,
} from '@tanstack/react-query'
import {
  PeopleContactsResponse,
  UserConnectionModel,
} from 'src/models/people.models'
import {
  ConnectionsListModel,
  GetConnectionsRequest,
} from 'src/queries/connections/connections.models'
import { getConnections } from 'src/queries/connections/connections.network'
import { getNextExtendPageParam } from 'src/queries/queries.utils'

/**
 * Typed query-key factory colocated with the factories. The family is
 * self-contained (no cross-feature invalidation), so the keys live here rather
 * than in `queries.constants.ts`. `params` is keyed verbatim so each distinct
 * search/filter combination is its own cached infinite query (resetting to
 * page 0), matching the legacy page-0-replaces behavior.
 */
export const connectionsQueryKeys = {
  all: () => ['connections'] as const,
  list: (params: GetConnectionsRequest) =>
    [...connectionsQueryKeys.all(), 'list', params] as const,
  infinite: (params: GetConnectionsRequest) =>
    [...connectionsQueryKeys.all(), 'infinite', params] as const,
}

/**
 * Flattens every fetched page's `userConnections` into model instances and
 * reports the latest page index — mirrors `selectOrgMembersListModel` in
 * members.queries so dropdown consumers get the same `{ list, page }` shape.
 */
const selectConnectionsListModel = (
  data: InfiniteData<PeopleContactsResponse, number>,
): ConnectionsListModel => ({
  connections: data.pages.flatMap((page) =>
    page.userConnections.map(
      (connection) => new UserConnectionModel(connection),
    ),
  ),
  page: data.pageParams.length - 1,
})

export const connectionsQueries = {
  /**
   * Single-page read of /connections. `params` carries the full query-param
   * surface (search/page/count/...). Used where a one-shot list is enough.
   */
  list: (params: GetConnectionsRequest = {}) =>
    queryOptions({
      queryKey: connectionsQueryKeys.list(params),
      queryFn: () => getConnections(params),
    }),
  /**
   * Infinite read for the contact dropdown (search + scroll). `gcTime: 0`
   * matches the legacy `useExtendInfiniteQuery` default so React Query does not
   * replay every cached page on return navigation. `page` is injected by
   * React Query via `pageParam`; the rest of `params` is forwarded verbatim.
   */
  infinite: (params: GetConnectionsRequest = {}) =>
    infiniteQueryOptions({
      queryKey: connectionsQueryKeys.infinite(params),
      queryFn: ({ pageParam }) => getConnections({ ...params, page: pageParam }),
      initialPageParam: 0,
      getNextPageParam: (lastPage) => getNextExtendPageParam(lastPage.pagination),
      select: selectConnectionsListModel,
      gcTime: 0,
    }),
}
